import ParticleLoader from "../ParticleLoader";
import React, { useEffect, useState } from "react";
import { Dialog } from "@headlessui/react";
import { useTranslation } from "react-i18next";
import {
    ArrowPathIcon,
    ExclamationTriangleIcon,
    MicrophoneIcon,
    UserIcon,
    XMarkIcon,
} from "@heroicons/react/24/outline";
import { useNavigate } from "react-router-dom";
import { getQuestionnaireResponses } from "../../Requests/QuestionnaireRequests";

/**
 * What respondents actually said.
 *
 * Voice answers show both the audio and its transcript — the transcript is what
 * feeds the analysis, so being able to check it against the recording matters.
 */
export default function QuestionnaireResponsesModal({ projectUuid, questionnaireUuid, onClose }) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [responses, setResponses] = useState(null);
    const [refreshing, setRefreshing] = useState(false);

    const load = () => {
        setRefreshing(true);
        return getQuestionnaireResponses(projectUuid, questionnaireUuid)
            .then((res) => setResponses(res.data))
            .catch(() => setResponses([]))
            .finally(() => setRefreshing(false));
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [questionnaireUuid]);

    const pending = (responses || []).some((response) =>
        (response.answers || []).some((answer) => answer.transcription_status === "pending")
    );

    return (
        <Dialog open onClose={onClose} className="relative z-50">
            <div className="fixed inset-0 bg-black/40" aria-hidden="true" />
            <div className="fixed inset-0 flex items-start justify-center overflow-y-auto p-4">
                <Dialog.Panel className="w-full max-w-3xl rounded-xl bg-white p-6 shadow-xl">
                    <div className="mb-4 flex items-start justify-between">
                        <div>
                            <Dialog.Title className="text-lg font-semibold text-gray-900">
                                {t("Questionnaire.Responses", "Responses")}
                            </Dialog.Title>
                            {responses && (
                                <p className="text-sm text-gray-500">
                                    {t("Questionnaire.ResponsesCount", "{{n}} response(s)", {
                                        n: responses.length,
                                    })}
                                </p>
                            )}
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                type="button"
                                onClick={load}
                                disabled={refreshing}
                                className="rounded-md p-1.5 text-gray-400 hover:text-gray-700 disabled:opacity-50"
                                title={t("Generic.Refresh", "Refresh")}
                            >
                                <ArrowPathIcon className={`h-5 w-5 ${refreshing ? "animate-spin" : ""}`} />
                            </button>
                            <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600">
                                <XMarkIcon className="h-5 w-5" />
                            </button>
                        </div>
                    </div>

                    {pending && (
                        <p className="mb-4 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-800">
                            {t(
                                "Questionnaire.AwaitingTranscription",
                                "Some recordings have not been transcribed yet. Press \u201cAnalyse responses\u201d to transcribe and analyse them."
                            )}
                        </p>
                    )}

                    {responses === null ? (
                        <div className="py-8 flex justify-center"><ParticleLoader size={40} /></div>
                    ) : responses.length === 0 ? (
                        <p className="py-8 text-center text-sm text-gray-500">
                            {t("Questionnaire.NoResponses", "No responses yet.")}
                        </p>
                    ) : (
                        <ul className="space-y-4">
                            {responses.map((response) => (
                                <li key={response.uuid} className="rounded-lg border border-gray-200 p-4">
                                    <div className="mb-3 flex items-center justify-between gap-3">
                                        <div className="flex min-w-0 items-center gap-2">
                                            <UserIcon className="h-4 w-4 flex-none text-gray-400" />
                                            <span className="truncate text-sm font-medium text-gray-900">
                                                {response.name}
                                            </span>
                                            {response.email && (
                                                <span className="truncate text-xs text-gray-500">
                                                    {response.email}
                                                </span>
                                            )}
                                        </div>
                                        <div className="flex flex-none items-center gap-2">
                                            {response.analysis_uuid && (
                                                <button
                                                    type="button"
                                                    onClick={() => navigate(`/analysis/${response.analysis_uuid}`)}
                                                    className="rounded-md border border-gray-300 px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50"
                                                >
                                                    {response.analysis_status === "Processing"
                                                        ? t("Questionnaire.AnalysisRunning", "Analysing…")
                                                        : t("Questionnaire.OpenAnalysis", "Open analysis")}
                                                </button>
                                            )}
                                            <span
                                                className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                                                    response.status === "completed"
                                                        ? "bg-green-100 text-green-800"
                                                        : "bg-gray-100 text-gray-700"
                                                }`}
                                            >
                                                {t(`Questionnaire.ResponseStatus.${response.status}`, response.status)}
                                            </span>
                                        </div>
                                    </div>

                                    <ul className="space-y-3">
                                        {(response.answers || []).map((answer) => (
                                            <li key={answer.question_uuid}>
                                                <p className="text-xs font-medium text-gray-500">
                                                    {answer.question_text}
                                                </p>

                                                {answer.is_voice ? (
                                                    <div className="mt-1 space-y-1.5">
                                                        {answer.audio_url && (
                                                            <audio
                                                                controls
                                                                src={answer.audio_url}
                                                                className="h-8 w-full max-w-md"
                                                            />
                                                        )}
                                                        {answer.transcription_status === "uploading" && (
                                                            <p className="flex items-center gap-1.5 text-xs text-gray-500">
                                                                <MicrophoneIcon className="h-3.5 w-3.5" />
                                                                {t(
                                                                    "Questionnaire.RecordingUploading",
                                                                    "Recording is still being saved"
                                                                )}
                                                            </p>
                                                        )}
                                                        {answer.transcription_status === "pending" && (
                                                            <p className="flex items-center gap-1.5 text-xs text-amber-700">
                                                                <MicrophoneIcon className="h-3.5 w-3.5" />
                                                                {t("Questionnaire.NotTranscribed", "Not transcribed yet")}
                                                            </p>
                                                        )}
                                                        {answer.transcription_status === "failed" && (
                                                            <p className="flex items-center gap-1.5 text-xs text-red-700">
                                                                <ExclamationTriangleIcon className="h-3.5 w-3.5" />
                                                                {t(
                                                                    "Questionnaire.TranscriptionFailed",
                                                                    "Transcription failed — the recording is still stored."
                                                                )}
                                                            </p>
                                                        )}
                                                        {answer.text && (
                                                            <p className="whitespace-pre-line text-sm text-gray-800">
                                                                {answer.text}
                                                            </p>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <p className="mt-0.5 whitespace-pre-line text-sm text-gray-800">
                                                        {answer.skipped || !answer.text ? (
                                                            <span className="italic text-gray-400">
                                                                {t("Questionnaire.Skipped", "Skipped")}
                                                            </span>
                                                        ) : (
                                                            answer.text
                                                        )}
                                                    </p>
                                                )}
                                            </li>
                                        ))}
                                    </ul>
                                </li>
                            ))}
                        </ul>
                    )}
                </Dialog.Panel>
            </div>
        </Dialog>
    );
}
